// frontend/src/pages/Storylines.js – Bookshelf Storylines
// Build: 2026-06-16-published-card-cleanup-v1

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import "./Storylines.css";
import { useUser } from "../UserContext";
import { loadStoredStories, saveStoredStories, mergeStories, isCanonApproved } from "../storyStorage";
import { api } from "../api";
import { TEXT } from "../i18n";
import { getPublicationInfo as getKnownPublicationInfo, getPublishedAuthorName } from "../utils/publicationStatus";
import StoryCoverCard from "../components/StoryCoverCard";
import StoryTeaserDialog from "../components/StoryTeaserDialog";
import { canOfferWriterRoom } from "../utils/storylineWriterRoomAccess";

/* =========================
   Tracking (Continue Reading)
   ========================= */

async function fetchTrackingBatch(storyIds) {
  const ids = Array.isArray(storyIds) ? storyIds.map((x) => String(x || "").trim()).filter(Boolean) : [];
  if (ids.length === 0) return {};

  const res = await api("/api/tracking/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ storyIds: ids }),
  });

  if (res && typeof res === "object" && res.summaries && typeof res.summaries === "object") {
    return res.summaries;
  }

  if (res && typeof res === "object") return res;
  return {};
}

const FORUMS = [
  "Action Forum",
  "Adventure Forum",
  "Children Forum",
  "Comedy Forum",
  "Crime Forum",
  "Fantasy Forum",
  "Historical Forum",
  "Inspirational Stories Forum",
  "Mystery Forum",
  "Poetry Forum",
  "Romance Forum",
  "Suspense / Thriller Forum",
  "Science Fiction Forum",
  "Faith & Inspiration Forum",
  "Humour Forum",
  "Non-Fiction Stories Forum",
  "Young Adult Forum",
  "Young Illustrators Gallery",
  "Comics & Illustrated Stories Forum",
  "NextScenes Originals",
  "Unclassified Forum",
];

const WRITING_MODE_OPTIONS = ["Solo", "Collaborative", "Group (Open)", "Group (Closed)"];

const BUILD_STAMP = "2026-05-05-visual-storytelling-protection-language-v1";

function shouldShowBuildStamp() {
  try {
    if (process.env.REACT_APP_SHOW_BUILD === "1") return true;
    return process.env.NODE_ENV !== "production";
  } catch {
    return false;
  }
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString() + " " + d.toLocaleTimeString();
}

function safeStr(v) {
  return typeof v === "string" ? v : "";
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function getStoryId(story) {
  return story?._id || story?.id || "";
}

function getAuthorInfo(story, t) {
  const authorObj =
    (story?.authorProfile && typeof story.authorProfile === "object" ? story.authorProfile : null) ||
    (story?.author && typeof story.author === "object" ? story.author : null);

  const authorString = typeof story?.author === "string" ? story.author : "";

  const authorName = safeStr(authorObj?.name) || safeStr(story?.authorName) || safeStr(story?.ownerName) || "";

  const authorUsername =
    safeStr(authorObj?.username) ||
    safeStr(story?.authorUsername) ||
    safeStr(story?.ownerUsername) ||
    safeStr(authorString) ||
    safeStr(story?.createdBy) ||
    "";

  const authorId = safeStr(authorObj?.id) || safeStr(story?.authorId) || safeStr(story?.ownerId) || "";

  const avatarUrl =
    safeStr(authorObj?.avatarUrl) ||
    safeStr(story?.authorAvatarUrl) ||
    safeStr(story?.authorAvatar) ||
    safeStr(story?.ownerAvatarUrl) ||
    safeStr(story?.ownerAvatar) ||
    "";

  const displayName = authorName || (authorUsername ? authorUsername : t.card_unknown_author);
  const handle = authorUsername ? `@${authorUsername}` : "";

  return { authorName, authorUsername, authorId, avatarUrl, displayName, handle };
}

function getStoryCreatedAt(story) {
  if (story?.createdAt) return story.createdAt;

  const scenes = safeArray(story?.scenes);
  if (!scenes.length) return null;

  let earliest = null;
  for (const s of scenes) {
    if (!s?.createdAt) continue;
    const tt = new Date(s.createdAt).getTime();
    if (Number.isNaN(tt)) continue;
    if (earliest === null || tt < earliest) earliest = tt;
  }
  return earliest ? new Date(earliest).toISOString() : null;
}

function getStoryLastModified(story) {
  let latest = story?.updatedAt ? new Date(story.updatedAt).getTime() : 0;
  const created = story?.createdAt ? new Date(story.createdAt).getTime() : 0;
  if (!Number.isNaN(created) && created > latest) latest = created;

  const scenes = safeArray(story?.scenes);
  if (scenes.length) {
    for (const s of scenes) {
      if (!s?.createdAt) continue;
      const tt = new Date(s.createdAt).getTime();
      if (!Number.isNaN(tt) && tt > latest) latest = tt;
    }
  }

  return latest ? new Date(latest).toISOString() : null;
}

function getCanonicalScenes(story) {
  const scenes = safeArray(story?.scenes);
  if (!scenes.length) return [];
  return scenes.filter(isCanonApproved);
}

function getCanonCount(story) {
  const c = story?.canonCount ?? story?.canonScenes ?? story?.canonApprovedCount ?? story?.canonApproved ?? null;
  const n = Number(c);
  if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
  return getCanonicalScenes(story).length;
}

function translateForumLabel(forumRaw, t) {
  const forum = safeStr(forumRaw).trim();
  if (!forum) return "";
  return t?.forum_labels?.[forum] || forum.replace(/\s+Forum$/i, "").trim();
}

function translateWritingModeLabel(modeRaw, t) {
  const mode = safeStr(modeRaw).trim();
  if (!mode) return "";
  return t?.writing_mode_labels?.[mode] || mode;
}


function compactMetaLine(story, t) {
  const parts = [];

  const forum = translateForumLabel(story?.forum, t);
  if (forum) parts.push(forum);

  const mode = translateWritingModeLabel(story?.writingMode, t);
  if (mode) parts.push(mode);

  const storyType = String(story?.storyType || "").toLowerCase();
  if (storyType === "short") {
    parts.push(t.shortStoryLabel || "Short Story");
  }

  const canonCount = getCanonCount(story);
  if (canonCount > 0) parts.push(`${canonCount} ${t.card_canon}`);

  return parts.join(" • ");
}

function getStoryTooltip(story, t) {
  const author = getAuthorInfo(story, t);
  const forum = translateForumLabel(story?.forum || "Unclassified Forum", t);
  const publicationInfo = getStoryPublicationInfo(story, t);

  const parts = [];
  parts.push(author.displayName);
  if (author.handle && !publicationInfo.isPublished) parts.push(author.handle);
  if (forum) parts.push(forum);

  // Published books should read like completed works, not workshop records.
  // Keep the forum/category, but do not surface writing mode, canon count,
  // created date, or last-updated development metadata in the card tooltip.
  if (publicationInfo.isPublished) {
    if (publicationInfo.publicationLabel) parts.push(publicationInfo.publicationLabel);
    return parts.filter(Boolean).join(" · ");
  }

  const mode = translateWritingModeLabel(story?.writingMode, t);
  const canonCount = getCanonCount(story);
  const createdAt = getStoryCreatedAt(story);
  const updatedAt = getStoryLastModified(story) || createdAt;

  if (mode) parts.push(mode);
  if (canonCount > 0) {
    parts.push(`${canonCount} ${canonCount === 1 ? t.card_canon_scene_singular : t.card_canon_scene_plural}`);
  }
  if (createdAt) parts.push(`${t.card_tooltip_created}: ${formatDateTime(createdAt)}`);
  if (updatedAt) parts.push(`${t.card_tooltip_updated}: ${formatDateTime(updatedAt)}`);

  return parts.filter(Boolean).join(" · ");
}

function AuthorAvatar({ story, t }) {
  const author = getAuthorInfo(story, t);

  if (author.avatarUrl) {
    return <img src={author.avatarUrl} alt={author.displayName} className="ns-storylines-avatar" />;
  }

  return (
    <div
      className="ns-storylines-avatar ns-storylines-avatar-fallback"
      title={author.displayName}
      aria-label={author.displayName}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ opacity: 0.85 }}>
        <path
          fill="currentColor"
          d="M12 12a4 4 0 1 0-4-4a4 4 0 0 0 4 4m0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5"
        />
      </svg>
    </div>
  );
}

function forumBandColor(forumRaw) {
  const f = String(forumRaw || "").toLowerCase();
  if (f.includes("mystery")) return "#0ea5e9";
  if (f.includes("crime")) return "#ef4444";
  if (f.includes("fantasy")) return "#a855f7";
  if (f.includes("romance")) return "#f43f5e";
  if (f.includes("poetry")) return "#22c55e";
  if (f.includes("science")) return "#38bdf8";
  if (f.includes("historical")) return "#f59e0b";
  if (f.includes("young illustrators") || f.includes("illustrator")) return "#0ea5e9";
  if (f.includes("comic") || f.includes("illustrated")) return "#7c3aed";
  if (f.includes("nextscenes originals") || f.includes("originals")) return "#b45309";
  if (f.includes("children")) return "#10b981";
  if (f.includes("non-fiction")) return "#16a34a";
  if (f.includes("humour")) return "#f97316";
  if (f.includes("thriller") || f.includes("suspense")) return "#111827";
  return "#0f766e";
}

/* =========================
   Cover helpers
   Order of precedence:
   1. Uploaded coverImageUrl
   2. Known title-based local covers
   3. Forum-based default cover
   ========================= */

function normalizeForumKey(forumRaw) {
  return String(forumRaw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\s*forum$/i, "")
    .trim();
}

function getDefaultCover(story) {
  const forum = normalizeForumKey(story?.forum);

  if (forum.includes("action")) return "/images/default-covers/action.jpg";
  if (forum.includes("adventure")) return "/images/default-covers/adventure.jpg";
  if (forum.includes("young illustrators") || forum.includes("illustrators gallery")) return "/images/default-covers/children.jpg";
  if (forum.includes("comics") || forum.includes("illustrated stories")) return "/images/default-cover.jpg";
  if (forum.includes("nextscenes originals") || forum.includes("originals")) return "/images/default-cover.jpg";
  if (forum.includes("children")) return "/images/default-covers/children.jpg";
  if (forum.includes("comedy")) return "/images/default-covers/comedy.jpg";
  if (forum.includes("crime")) return "/images/default-covers/crime.jpg";
  if (forum.includes("fantasy")) return "/images/default-covers/fantasy.jpg";
  if (forum.includes("historical")) return "/images/default-covers/historical.jpg";
  if (forum.includes("inspirational stories")) return "/images/default-covers/inspirational.jpg";
  if (forum.includes("faith") || forum.includes("inspiration")) return "/images/default-covers/faith-inspiration.jpg";
  if (forum.includes("mystery")) return "/images/default-covers/mystery.jpg";
  if (forum.includes("poetry")) return "/images/default-covers/poetry.jpg";
  if (forum.includes("romance")) return "/images/default-covers/romance.jpg";
  if (forum.includes("suspense") || forum.includes("thriller")) return "/images/default-covers/thriller.jpg";
  if (forum.includes("science fiction")) return "/images/default-covers/science-fiction.jpg";
  if (forum.includes("humour") || forum.includes("humor")) return "/images/default-covers/humour.jpg";
  if (forum.includes("non-fiction")) return "/images/default-covers/non-fiction.jpg";
  if (forum.includes("young adult")) return "/images/default-covers/young-adult.jpg";
  if (forum.includes("unclassified")) return "/images/default-covers/unclassified.jpg";

  return "/images/default-cover.jpg";
}

function getKnownLocalCoverByTitle(story) {
  const title = String(story?.title || "").trim().toLowerCase();

  if (title.includes("jar lingo")) return "/images/covers/king-jar-lingo-evel-broda-cover.jpg";
  if (title.includes("didie")) return "/images/covers/didie-cover.jpg";
  if (title.includes("réflexions du voyageur")) return "/images/covers/reflexions-du-voyageur-fr.webp";
  if (title.includes("reflections of the wayfarer")) return "/images/covers/reflections-of-the-wayfarer-en.webp";

  return "";
}

function getStoryCover(story) {
  const uploadedCover = safeStr(story?.coverImageUrl).trim();
  if (uploadedCover) return uploadedCover;

  const knownLocalCover = getKnownLocalCoverByTitle(story);
  if (knownLocalCover) return knownLocalCover;

  return getDefaultCover(story);
}


function getPublishedDisplayAuthorName(story, fallback) {
  return getPublishedAuthorName(story, fallback);
}

function getStoryPublicationInfo(story, t) {
  const known = getKnownPublicationInfo(story, t) || {};

  if (!known.isPublished) {
    return {
      ...known,
      isPublished: false,
      publicationLabel:
        known.publicationLabel ||
        t.status_in_development ||
        "In Development",
      developmentLabel:
        known.developmentLabel ||
        t.status_not_published ||
        "Not Yet Published",
      cta: known.cta || t.btn_view_book || "View Book",
      amazonHref: known.amazonHref || "",
    };
  }

  return {
    ...known,
    isPublished: true,
    publicationLabel:
      known.publicationLabel ||
      t.status_published_book ||
      "Published Book",
    developmentLabel:
      known.developmentLabel ||
      t.status_preview_on_nextscenes ||
      "Preview on NextScenes",
    cta:
      known.cta ||
      t.btn_view_on_amazon ||
      "View publication",
    amazonHref: safeStr(known.amazonHref).trim(),
  };
}

function getStorylineLibraryTitle(lang, t) {
  if (lang === "fr") return "Bibliothèque NextScenes";
  return "NextScenes Story Library";
}

function getStorylineLibrarySubtitle(lang, effectiveForum, t) {
  if (effectiveForum) {
    const forumName = translateForumLabel(effectiveForum, t);
    return lang === "fr"
      ? `Histoires classées dans ${forumName}.`
      : `Stories filed under ${forumName}.`;
  }

  return lang === "fr"
    ? "Explorez gratuitement les œuvres publiées, les aperçus contrôlés et les histoires encore en développement."
    : "Explore published works, controlled previews, and storylines still growing on NextScenes for free.";
}

function getStorylineLibraryNote(lang) {
  if (lang === "fr") {
    return "Les livres publiés sont présentés comme des œuvres terminées ou des aperçus. L’inscription, la lecture et la participation de base sont gratuites.";
  }

  return "Published books are shown as completed works or previews. Registration, reading, and core participation are free.";
}

function getPublishedMetaLine(story, t) {
  const parts = [];

  const forum = translateForumLabel(story?.forum, t);
  if (forum) parts.push(forum);

  const storyType = String(story?.storyType || "").trim().toLowerCase();
  if (storyType === "short") parts.push(t.shortStoryLabel || "Short Story");

  return parts.filter(Boolean).join(" • ");
}

function getVisualStoryLaneNotice(forumRaw, t) {
  const forum = safeStr(forumRaw).trim();
  if (!forum) return null;
  return t?.visual_story_lane_notices?.[forum] || null;
}

function StorylinesForumNotice({ forum, t }) {
  const notice = getVisualStoryLaneNotice(forum, t);
  if (!notice) return null;

  const accent = forumBandColor(forum);

  return (
    <section
      className="ns-card storylines-visual-lane-notice"
      role="note"
      style={{
        margin: "0 0 18px",
        padding: "16px 18px",
        borderRadius: 22,
        border: `1px solid ${accent}33`,
        background:
          "linear-gradient(180deg, rgba(255,253,247,0.98) 0%, rgba(248,244,235,0.94) 100%)",
        boxShadow: "0 14px 34px rgba(15,23,42,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          color: "#102033",
          fontWeight: 900,
          lineHeight: 1.25,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 11,
            height: 11,
            borderRadius: 999,
            background: accent,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        {notice.title}
      </div>
      <p style={{ margin: "8px 0 0", color: "rgba(15,23,42,0.78)", lineHeight: 1.6, fontSize: 14 }}>
        {notice.body}
      </p>
      {notice.guardrail ? (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            color: "#064e3b",
            fontWeight: 900,
            fontSize: 13.5,
            lineHeight: 1.35,
          }}
        >
          {notice.guardrail}
        </div>
      ) : null}
    </section>
  );
}

function StoryIdentityPlateInline({ story, t }) {
  const title = safeStr(story?.title).trim() || t.card_untitled_story;
  const author = getAuthorInfo(story, t);
  const band = forumBandColor(story?.forum);

  const forumLabel = translateForumLabel(story?.forum, t);
  const modeLabel = translateWritingModeLabel(story?.writingMode, t);

  return (
    <div
      className="ns-story-identity-plate"
      aria-label="Story identity"
      style={{
        width: 140,
        minWidth: 140,
        height: 176,
        borderRadius: 14,
        overflow: "hidden",
        background: "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)",
        border: "1px solid rgba(15,23,42,0.10)",
        boxShadow: "0 8px 20px rgba(2,6,23,0.08)",
        display: "flex",
        flexDirection: "column",
      }}
      title={title}
    >
      <div style={{ height: 12, background: band }} />
      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            fontWeight: 900,
            color: "#ffffff",
            fontSize: 11.5,
            lineHeight: "14px",
            letterSpacing: 0.1,
            textTransform: "uppercase",
            opacity: 0.96,
          }}
        >
          {title.length > 42 ? `${title.slice(0, 42)}…` : title}
        </div>

        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, lineHeight: "13px" }}>{author.displayName}</div>

        {(forumLabel || modeLabel) && (
          <div style={{ marginTop: 6, color: "rgba(255,255,255,0.68)", fontSize: 11, lineHeight: "13px" }}>
            {forumLabel ? forumLabel : t.card_story}
            {modeLabel ? <span> • {modeLabel}</span> : null}
          </div>
        )}
      </div>

      <div style={{ marginTop: "auto", padding: 10 }}>
        <div
          style={{
            height: 32,
            borderRadius: 12,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.10)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.80)",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.1,
          }}
        >
          {t.card_a_nextscenes_story}
        </div>
      </div>
    </div>
  );
}

/* =========================
   Step 5: Skeleton UI
   ========================= */

function SkeletonLine({ w = "100%", h = 12, r = 10, style }) {
  return <div className="ns-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

function SkeletonIdentityPlate() {
  return (
    <div
      style={{
        width: 140,
        minWidth: 140,
        height: 176,
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid rgba(15,23,42,0.08)",
        background: "rgba(248,250,252,0.95)",
        boxShadow: "0 8px 20px rgba(2,6,23,0.04)",
      }}
    >
      <div className="ns-skel" style={{ height: 12, borderRadius: 0 }} />
      <div style={{ padding: 10, display: "grid", gap: 10 }}>
        <SkeletonLine w="86%" h={12} />
        <SkeletonLine w="72%" h={11} />
        <SkeletonLine w="65%" h={11} />
      </div>
      <div style={{ marginTop: 18, padding: 10 }}>
        <div className="ns-skel" style={{ height: 32, borderRadius: 12 }} />
      </div>
    </div>
  );
}

function SkeletonStorylineCard() {
  return (
    <article className={"ns-card storyline-card"} aria-label="Loading storyline">
      <div className="storyline-card-main">
        <div className="storyline-cover-slot">
          <SkeletonIdentityPlate />
        </div>

        <div className="storyline-card-text">
          <div className="storyline-byline" style={{ marginTop: 2 }}>
            <div className="ns-skel" style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <SkeletonLine w="78%" h={12} />
              <div style={{ marginTop: 8 }}>
                <SkeletonLine w="55%" h={11} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <SkeletonLine w="82%" h={11} />
            <div style={{ marginTop: 10 }}>
              <SkeletonLine w="64%" h={11} />
            </div>
          </div>

          <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
            <SkeletonLine w="74%" h={11} />
            <SkeletonLine w="68%" h={11} />
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="ns-skel" style={{ height: 34, borderRadius: 10 }} />
          </div>
        </div>
      </div>

      <div className="storyline-card-actions">
        <div className="ns-skel" style={{ height: 36, width: 128, borderRadius: 999 }} />
        <div className="ns-skel" style={{ height: 36, width: 120, borderRadius: 999 }} />
        <div className="ns-skel" style={{ height: 36, width: 120, borderRadius: 999 }} />
      </div>
    </article>
  );
}

/* =========================
   Welcome panel
   ========================= */

function StorylinesWelcomePanel({ onClose, onCreate, onPropose, onShelf, onExplore }) {
  const lang = (typeof window !== "undefined" && window.localStorage.getItem("ns.lang")) || "en";
  const t = TEXT[lang] || TEXT.en;

  return (
    <div
      className="ns-card"
      style={{
        padding: 18,
        borderRadius: 14,
        marginBottom: 14,
        border: "1px solid rgba(15,23,42,0.10)",
        background: "rgba(248,250,252,0.94)",
        boxShadow: "0 10px 24px rgba(2,6,23,0.06)",
      }}
      aria-label={t.welcome_title}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>{t.welcome_title}</div>

          <div style={{ marginTop: 6, color: "rgba(15,23,42,0.80)", lineHeight: "18px", fontSize: 13 }}>
            {t.welcome_action}
          </div>
        </div>

        <button
          type="button"
          className="ns-button ns-button-outline"
          onClick={onClose}
          style={{ height: 34, padding: "0 12px", borderRadius: 999, flexShrink: 0 }}
          title={t.btn_close}
        >
          {t.btn_close}
        </button>
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button type="button" className="ns-button ns-button-primary" onClick={onCreate}>
          {t.btn_create}
        </button>

        <button type="button" className="ns-button ns-button-outline" onClick={onPropose}>
          {t.btn_continue}
        </button>

        <button type="button" className="ns-button ns-button-outline" onClick={onExplore}>
          {t.btn_explore}
        </button>

        <button type="button" className="ns-button ns-button-outline" onClick={onShelf}>
          {t.btn_shelf}
        </button>
      </div>

      <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 700, color: "rgba(15,23,42,0.72)" }}>
        {t.tagline}
      </div>
    </div>
  );
}

export default function Storylines({ selectedForum, onNavigatePage }) {
  const lang = (typeof window !== "undefined" && window.localStorage.getItem("ns.lang")) || "en";
  const t = TEXT[lang] || TEXT.en;

  const { isAuthenticated, user, username } = useUser();
  const showBuildStamp = useMemo(() => shouldShowBuildStamp(), []);

  const [stories, setStories] = useState([]);
  const [forumFilter, setForumFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  // Deep links from the public homepage use the existing search field.
  // A title query never overrides the Storylines visibility/access controls.
  const [search, setSearch] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("q")?.trim().slice(0, 120) || "";
    } catch { return ""; }
  });
  const [sortBy, setSortBy] = useState("recent");
  const [page, setPage] = useState(1);

  const [navStoryId, setNavStoryId] = useState(null);
  const [navForum, setNavForum] = useState("");

  const [flashStoryId, setFlashStoryId] = useState(null);
  const flashTimerRef = useRef(null);

  const [selectedCardId, setSelectedCardId] = useState(null);
  const [teaserStoryId, setTeaserStoryId] = useState(null);
  const dismissTeaser = useCallback(() => setTeaserStoryId(null), []);

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  const [shelfSavedById, setShelfSavedById] = useState({});
  const [shelfBusyId, setShelfBusyId] = useState(null);

  const [shelfSyncTick, setShelfSyncTick] = useState(0);
  const [trackingSyncTick, setTrackingSyncTick] = useState(0);

  const [trackingByStoryId, setTrackingByStoryId] = useState({});
  const [, setTrackingHydrating] = useState(false);
  const [, setTrackingError] = useState("");

  const [busyWriterById, setBusyWriterById] = useState({});
  const [busyReaderById, setBusyReaderById] = useState({});

  const [cardFeedbackById, setCardFeedbackById] = useState({});
  const feedbackTimersRef = useRef({});

  const PAGE_SIZE = 9;

  function bumpShelfStamp() {
    try {
      window.localStorage.setItem("ns.shelf.changedAt", String(Date.now()));
    } catch {}
  }

  function readNavHintsFromStorage() {
    try {
      const storedStoryId = window.localStorage.getItem("ns.selectedStoryId");
      const storedForum = window.localStorage.getItem("ns.selectedForum");
      return {
        storedStoryId: storedStoryId ? String(storedStoryId) : "",
        storedForum: storedForum ? String(storedForum) : "",
      };
    } catch {
      return { storedStoryId: "", storedForum: "" };
    }
  }

  function clearSelectedStoryIdInStorage() {
    try {
      window.localStorage.removeItem("ns.selectedStoryId");
    } catch {}
  }

  function dismissWelcome() {
    try {
      window.localStorage.setItem("ns.storylines.welcome.dismissed", "1");
    } catch {}
    setShowWelcome(false);
    setWelcomeDismissed(true);
  }

  function welcomeCreate() {
    if (onNavigatePage) return onNavigatePage("storyHub");
    window.location.href = "/storyHub";
  }

  function welcomePropose() {
    if (onNavigatePage) return onNavigatePage("writerStudio");
    window.location.href = "/writerStudio";
  }

  function welcomeShelf() {
    if (onNavigatePage) return onNavigatePage("myShelf");
    window.location.href = "/myShelf";
  }

  function reinstateWelcome() {
    try {
      window.localStorage.removeItem("ns.storylines.welcome.dismissed");
    } catch {}
    setShowWelcome(true);
    setWelcomeDismissed(false);
  }

  function welcomeExplore() {
    try {
      window.scrollTo({ top: 420, behavior: "smooth" });
    } catch {}
  }

  function setCardFeedback(storyIdRaw, payload, ttlMs = 2200) {
    const sid = String(storyIdRaw || "").trim();
    if (!sid) return;

    setCardFeedbackById((prev) => ({ ...(prev || {}), [sid]: payload }));

    try {
      const timers = feedbackTimersRef.current || {};
      if (timers[sid]) clearTimeout(timers[sid]);
      timers[sid] = setTimeout(() => {
        setCardFeedbackById((prev) => {
          const next = { ...(prev || {}) };
          delete next[sid];
          return next;
        });
        const t2 = feedbackTimersRef.current || {};
        delete t2[sid];
      }, Math.max(800, Number(ttlMs) || 2200));
      feedbackTimersRef.current = timers;
    } catch {}
  }

  function clearAllFeedbackTimers() {
    try {
      const timers = feedbackTimersRef.current || {};
      Object.keys(timers).forEach((k) => {
        try {
          clearTimeout(timers[k]);
        } catch {}
      });
      feedbackTimersRef.current = {};
    } catch {}
  }

  useEffect(() => {
    loadStories();

    try {
      const dismissed = window.localStorage.getItem("ns.storylines.welcome.dismissed");
      const isDismissed = String(dismissed || "") === "1";
      setWelcomeDismissed(isDismissed);
      if (!isDismissed) setShowWelcome(true);
    } catch {}

    const { storedStoryId, storedForum } = readNavHintsFromStorage();
    if (storedStoryId) setNavStoryId(storedStoryId);

    if (!selectedForum && storedForum) {
      setForumFilter(storedForum);
      setNavForum(storedForum);
    }

    function onStorage(e) {
      if (!e) return;

      if (e.key === "ns.selectedStoryId" && e.newValue) setNavStoryId(String(e.newValue));
      if (e.key === "ns.selectedForum" && e.newValue && !selectedForum) {
        setForumFilter(String(e.newValue));
        setNavForum(String(e.newValue));
      }

      if (e.key === "ns.shelf.changedAt") setShelfSyncTick((tt) => tt + 1);
      if (e.key === "ns.tracking.changedAt") setTrackingSyncTick((tt) => tt + 1);
    }

    window.addEventListener("storage", onStorage);

    function onFocus() {
      try {
        const _s = window.localStorage.getItem("ns.shelf.changedAt") || "";
        const _tr = window.localStorage.getItem("ns.tracking.changedAt") || "";
        if (_s) setShelfSyncTick((x) => x + 1);
        if (_tr) setTrackingSyncTick((x) => x + 1);
      } catch {}
    }
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = null;
      }
      clearAllFeedbackTimers();
    };
  }, []);

  useEffect(() => {
    if (selectedForum) setForumFilter(selectedForum);
  }, [selectedForum]);

  function overlayStoredIntoRemoteList(stored, remote) {
    const storedArr = safeArray(stored);
    const remoteArr = safeArray(remote);

    const byId = new Map();
    storedArr.forEach((s) => {
      const id = getStoryId(s);
      if (id) byId.set(String(id), s);
    });

    return remoteArr.map((r) => {
      const id = getStoryId(r);
      const s = id ? byId.get(String(id)) : null;

      const remoteScenes = safeArray(r?.scenes);
      const storedScenes = safeArray(s?.scenes);

      const scenes = remoteScenes.length > 0 ? remoteScenes : storedScenes.length > 0 ? storedScenes : [];

      return { ...(s || {}), ...(r || {}), scenes };
    });
  }

  async function loadStories() {
    setLoading(true);
    setLoadErr("");

    const stored = loadStoredStories() || [];

    try {
      // The backend caps each list request at 100. Walk authorized pages
      // so Writer A sees all accessible stories, not just the first DB page.
      const remoteArr = [];
      const seenRemoteIds = new Set();
      for (let serverPage = 1; serverPage <= 100; serverPage += 1) {
        const batch = safeArray(await api(
          `/api/stories?mode=list&page=${serverPage}&limit=100`,
          { method: "GET" }
        ));
        for (const story of batch) {
          const id = String(getStoryId(story) || "");
          if (!id || seenRemoteIds.has(id)) continue;
          seenRemoteIds.add(id);
          remoteArr.push(story);
        }
        if (batch.length < 100) break;
      }

      if (remoteArr.length >= 0) {
        let base = remoteArr;

        try {
          // mergeStories(remote, stored): server data must win over stale local metadata.
          // In particular, a newly saved Story Teaser must not revert to a cached "".
          const m2 = mergeStories(remoteArr, stored);
          if (Array.isArray(m2) && m2.length) {
            const remoteIds = new Set(remoteArr.map((x) => String(getStoryId(x))));
            base = m2.filter((x) => remoteIds.has(String(getStoryId(x))));
          }
        } catch {}

        const finalList = overlayStoredIntoRemoteList(stored, base);

        setStories(finalList);
        saveStoredStories(finalList);
      }
    } catch (e) {
      setStories(safeArray(stored));
      setLoadErr(String(e?.message || "Failed to load storylines."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const { storedStoryId } = readNavHintsFromStorage();
    if (storedStoryId && storedStoryId !== String(navStoryId || "")) setNavStoryId(storedStoryId);
  }, [stories.length, forumFilter, modeFilter, search, sortBy]);

  const filtered = useMemo(() => {
    let result = stories.filter((story) => {
      if (forumFilter && story?.forum !== forumFilter) return false;

      if (modeFilter) {
        const mode = String(story?.writingMode || "").toLowerCase();
        if (!mode.includes(modeFilter.toLowerCase())) return false;
      }

      if (typeFilter === "short") {
        if (String(story?.storyType || "").toLowerCase() !== "short") return false;
      }

      if (search.trim()) {
        const s = search.toLowerCase();
        const titleText = String(story?.title || "").toLowerCase();
        const a = getAuthorInfo(story, t);
        const dn = String(a.displayName || "").toLowerCase();
        const h = String(a.handle || "").toLowerCase();
        if (!titleText.includes(s) && !dn.includes(s) && !h.includes(s)) return false;
      }

      return true;
    });

    result = [...result].sort((a, b) => {
      if (sortBy === "title") {
        return String(a?.title || "")
          .toLowerCase()
          .localeCompare(String(b?.title || "").toLowerCase());
      }
      if (sortBy === "canon") {
        const ca = getCanonCount(a);
        const cb = getCanonCount(b);
        if (cb !== ca) return cb - ca;
      }

      const da = getStoryLastModified(a) || getStoryCreatedAt(a) || "";
      const db = getStoryLastModified(b) || getStoryCreatedAt(b) || "";
      return String(db || "").localeCompare(String(da || ""));
    });

    return result;
  }, [stories, forumFilter, modeFilter, typeFilter, search, sortBy, t]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const pageStories = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const teaserStory = teaserStoryId
    ? stories.find((story) => String(getStoryId(story)) === String(teaserStoryId))
    : null;

  const shelfStatusIdsKey = useMemo(() => {
    const ids = filtered
      .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      .map((st) => String(getStoryId(st) || ""))
      .filter(Boolean);

    ids.sort();
    return ids.join(",");
  }, [filtered, page]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShelfSavedById({});
      return;
    }

    if (!shelfStatusIdsKey) {
      setShelfSavedById({});
      return;
    }

    const ids = shelfStatusIdsKey.split(",").filter(Boolean);
    let cancelled = false;

    async function loadShelfStatus() {
      try {
        const q = ids.map((id) => encodeURIComponent(id)).join(",");
        const res = await api(`/api/library/shelf/status?ids=${q}`, { method: "GET" });

        const map = res && typeof res === "object" ? res.savedById : null;
        if (cancelled) return;

        if (map && typeof map === "object") {
          setShelfSavedById(map);
        } else {
          const fallback = {};
          ids.forEach((id) => {
            fallback[id] = false;
          });
          setShelfSavedById(fallback);
        }
      } catch {
        if (cancelled) return;
        const fallback = {};
        ids.forEach((id) => {
          fallback[id] = false;
        });
        setShelfSavedById(fallback);
      }
    }

    loadShelfStatus();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, shelfStatusIdsKey, shelfSyncTick]);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) {
        setTrackingByStoryId({});
        setTrackingHydrating(false);
        setTrackingError("");
        return;
      }

      const ids = safeArray(stories)
        .map((s) => String(getStoryId(s) || "").trim())
        .filter(Boolean);

      if (ids.length === 0) {
        setTrackingByStoryId({});
        return;
      }

      try {
        setTrackingHydrating(true);
        setTrackingError("");

        const batch = ids.slice(0, 350);
        const summaries = await fetchTrackingBatch(batch);

        if (summaries && typeof summaries === "object") {
          setTrackingByStoryId(summaries);
        } else {
          setTrackingByStoryId({});
        }
      } catch {
        setTrackingError(t.feedback_tracking_load_failed);
        setTrackingByStoryId({});
      } finally {
        setTrackingHydrating(false);
      }
    })();
  }, [isAuthenticated, stories.length, trackingSyncTick, t]);

  async function toggleShelf(storyIdRaw) {
    const storyId = String(storyIdRaw || "");
    if (!storyId) return;

    if (!isAuthenticated) {
      setCardFeedback(storyId, { type: "info", text: t.feedback_login_required_shelf }, 2400);
      goLogin("shelf_save_guest");
      return;
    }

    if (shelfBusyId && String(shelfBusyId) === storyId) return;

    const currentlySaved = !!shelfSavedById?.[storyId];
    setShelfBusyId(storyId);

    setShelfSavedById((prev) => ({ ...(prev || {}), [storyId]: !currentlySaved }));

    try {
      if (currentlySaved) {
        await api(`/api/library/shelf/${encodeURIComponent(storyId)}/remove`, { method: "POST" });
        setCardFeedback(storyId, { type: "ok", text: t.feedback_removed_from_shelf }, 1800);
      } else {
        await api(`/api/library/shelf/${encodeURIComponent(storyId)}/save`, { method: "POST" });
        setCardFeedback(storyId, { type: "ok", text: t.feedback_saved_to_shelf }, 1800);
      }

      bumpShelfStamp();
    } catch {
      setShelfSavedById((prev) => ({ ...(prev || {}), [storyId]: currentlySaved }));
      setCardFeedback(storyId, { type: "err", text: t.feedback_shelf_action_failed }, 2600);
    } finally {
      setShelfBusyId(null);
    }
  }

  useEffect(() => {
    if (!navStoryId || filtered.length === 0) return;

    const idx = filtered.findIndex((s) => String(getStoryId(s)) === String(navStoryId));
    if (idx === -1) return;

    const neededPage = Math.floor(idx / PAGE_SIZE) + 1;
    if (page !== neededPage) {
      setPage(neededPage);
      return;
    }

    const el = document.getElementById(`story-card-${navStoryId}`);
    if (!el) return;

    el.scrollIntoView({ block: "center", behavior: "smooth" });

    setFlashStoryId(String(navStoryId));
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setFlashStoryId(null);
      flashTimerRef.current = null;
    }, 3500);

    clearSelectedStoryIdInStorage();
    setNavStoryId(null);
    setNavForum("");
  }, [navStoryId, filtered, page]);

  function goLogin(reason = "login_required") {
    try {
      window.localStorage.setItem("ns.auth.intent", reason);
    } catch {}
    if (onNavigatePage) onNavigatePage("login");
    else alert(t.alert_login_first);
  }

  function enterWriter(story) {
    if (!onNavigatePage) return;
    if (!canOfferWriterRoom(story, { isAuthenticated, user, username })) return;

    const id = getStoryId(story);
    if (!id) return;

    if (!isAuthenticated) {
      setCardFeedback(id, { type: "info", text: t.feedback_login_required_writer_room }, 2400);
      goLogin("writer_room_blocked_guest");
      return;
    }

    setBusyWriterById((prev) => ({ ...(prev || {}), [id]: true }));
    setCardFeedback(id, { type: "info", text: t.feedback_opening_writer_room }, 1400);

    try {
      window.localStorage.setItem("ns.selectedStoryId", String(id));
    } catch {}

    onNavigatePage("writerStudio");

    window.setTimeout(() => {
      setBusyWriterById((prev) => {
        const next = { ...(prev || {}) };
        delete next[id];
        return next;
      });
    }, 1200);
  }

  function readerView(story) {
    const id = getStoryId(story);
    if (!id) return;

    setBusyReaderById((prev) => ({ ...(prev || {}), [id]: true }));
    setCardFeedback(id, { type: "info", text: t.feedback_opening_reader_view }, 1200);

    try {
      window.localStorage.setItem("ns.selectedStoryId", String(id));
      window.localStorage.setItem("ns.reader.focusStoryId", String(id));
    } catch {}

    if (onNavigatePage) {
      onNavigatePage("readerView", { storyId: id });
      window.setTimeout(() => {
        setBusyReaderById((prev) => {
          const next = { ...(prev || {}) };
          delete next[id];
          return next;
        });
      }, 1200);
      return;
    }

    window.location.href = `/reader-view?storyId=${encodeURIComponent(id)}`;
  }

  function continueReaderView(story) {
    const id = getStoryId(story);
    if (!id) return;

    try {
      window.localStorage.setItem("ns.reader.autoOpenValidated", "1");
      window.localStorage.setItem("ns.reader.autoOpenStoryId", String(id));
    } catch {}

    readerView(story);
  }

  const effectiveForum = selectedForum || navForum || forumFilter || "";
  const breadcrumbSegments = [{ label: t.breadcrumb_home, page: "home" }];
  breadcrumbSegments.push({ label: t.breadcrumb_forums, page: "forums" });
  if (effectiveForum) breadcrumbSegments.push({ label: translateForumLabel(effectiveForum, t).toUpperCase(), page: "storylines" });
  breadcrumbSegments.push({ label: t.breadcrumb_storylines, page: null });

  const showGridSkeletons = loading && safeArray(stories).length === 0;
  const skeletonCount = 9;

  return (
    <div className="ns-page storylines-page">
      <div className="ns-breadcrumb-trail">
        {breadcrumbSegments.map((seg, idx) => (
          <span key={seg.label}>
            {idx > 0 && <span className="ns-breadcrumb-separator">/</span>}
            {seg.page && onNavigatePage ? (
              <button className="ns-breadcrumb-link" onClick={() => onNavigatePage(seg.page)}>
                {seg.label}
              </button>
            ) : (
              <span className="ns-breadcrumb-current">{seg.label}</span>
            )}
          </span>
        ))}
      </div>

      <header
        className="ns-page-header storylines-hero ns-identity-hero"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url("/images/storyhub/storyhub-background.png")',
        }}
      >
        <div className="ns-identity-hero-copy storylines-hero-copy">
          <div className="ns-identity-kicker ns-gold-kicker storylines-hero-kicker">NEXTSCENES</div>

          <h1 className="ns-page-title ns-identity-title">{getStorylineLibraryTitle(lang, t)}</h1>

          <p className="ns-page-subtitle ns-identity-subtitle">
            {getStorylineLibrarySubtitle(lang, effectiveForum, t)}
          </p>

          <p className="ns-card-meta storylines-hero-note">
            {getStorylineLibraryNote(lang)}
          </p>

          <div className="ns-identity-actions storylines-hero-actions">
            <button className="ns-button ns-button-outline" type="button" onClick={loadStories} disabled={loading}>
              {loading ? t.loading_list : t.refresh}
            </button>

            {!showWelcome && welcomeDismissed ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={reinstateWelcome}
                title={t.title_show_welcome_again}
              >
                {t.welcome_guide}
              </button>
            ) : null}

            {loadErr ? <span className="ns-card-meta ns-identity-status is-error">{loadErr}</span> : null}

            {loading ? <span className="ns-card-meta ns-identity-status">{t.loading_list}</span> : null}
          </div>
        </div>

        <div className="ns-identity-hero-stat storylines-hero-stat" aria-live="polite">
          <span className="ns-identity-stat-number">{filtered.length}</span>
          <span className="ns-identity-stat-label">
            {filtered.length === 1 ? (t.card_story || "Story") : (t.breadcrumb_storylines || "Storylines")}
          </span>
        </div>
      </header>

      {effectiveForum ? <StorylinesForumNotice forum={effectiveForum} t={t} /> : null}

      {showWelcome ? (
        <StorylinesWelcomePanel
          onClose={dismissWelcome}
          onCreate={welcomeCreate}
          onPropose={welcomePropose}
          onShelf={welcomeShelf}
          onExplore={welcomeExplore}
        />
      ) : null}

      <section className="storylines-filters-wrap">
        <div className="storylines-filters-row storylines-filters-row-polished">
          <input
            className="ns-input storylines-filter-input"
            placeholder={t.search_title_author}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          <select
            className="ns-input storylines-filter-input"
            value={forumFilter}
            onChange={(e) => {
              setForumFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t.all_forums}</option>
            {FORUMS.map((f) => (
              <option key={f} value={f}>
                {translateForumLabel(f, t)}
              </option>
            ))}
          </select>

          <select
            className="ns-input storylines-filter-input"
            value={modeFilter}
            onChange={(e) => {
              setModeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t.all_writing_modes}</option>
            {WRITING_MODE_OPTIONS.map((mode) => (
              <option key={mode} value={mode}>
                {translateWritingModeLabel(mode, t)}
              </option>
            ))}
          </select>

          <select
            className="ns-input storylines-filter-input"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t.filterAll || "All Stories"}</option>
            <option value="short">{t.shortStoryLabel || "Short Story"}</option>
          </select>

          <select
            className="ns-input storylines-filter-input"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
          >
            <option value="recent">{t.sort_recent}</option>
            <option value="title">{t.sort_title}</option>
            <option value="canon">{t.sort_canon}</option>
          </select>
        </div>
      </section>

      <section className="ns-column">
        <div className="storylines-grid">
          {showGridSkeletons ? Array.from({ length: skeletonCount }).map((_, i) => <SkeletonStorylineCard key={`sk-${i}`} />) : null}

          {!showGridSkeletons &&
            pageStories.map((story) => {
              const storyId = getStoryId(story);
              const tooltip = getStoryTooltip(story, t);

              const createdAt = getStoryCreatedAt(story);
              const updatedAt = getStoryLastModified(story) || createdAt;

              const publicationInfo = getStoryPublicationInfo(story, t);
              const author = getAuthorInfo(story, t);
              const displayAuthor = publicationInfo?.isPublished
                ? { ...author, displayName: getPublishedDisplayAuthorName(story, author.displayName), authorUsername: "" }
                : author;

              const isActiveFlash = flashStoryId && String(storyId) === String(flashStoryId);
              const isSelected = selectedCardId && String(storyId) === String(selectedCardId);

              const isGuestLocked = !isAuthenticated;
               const writerRoomAllowed = canOfferWriterRoom(story, { isAuthenticated, user, username });

              const isSaved = !!shelfSavedById?.[String(storyId)];
              const isShelfBusy = shelfBusyId && String(shelfBusyId) === String(storyId);

              const isWriterBusy = !!busyWriterById?.[String(storyId)];
              const isReaderBusy = !!busyReaderById?.[String(storyId)];

              const tracking = isAuthenticated ? trackingByStoryId?.[String(storyId)] : null;
              const hasProgress = !!tracking?.lastReadAt;
              const isUpdated = !!tracking?.updated;
              const readerPrimaryLabel = publicationInfo.isPublished ? (t.btn_view_published_book || t.btn_view_book || t.btn_reader_view) : t.btn_reader_view;
              const readerHint = hasProgress ? (isUpdated ? t.btn_continue_updated : t.btn_continue_only) : "";

              const anyBusy = isShelfBusy || isWriterBusy || isReaderBusy;
              const hasStoryTeaser = typeof story?.storyTeaser === "string" && !!story.storyTeaser.trim();

              const feedback = cardFeedbackById?.[String(storyId)] || null;
              const feedbackColor =
                feedback?.type === "err" ? "#b91c1c" : feedback?.type === "ok" ? "#065f46" : "rgba(15,23,42,0.72)";

              return (
                <article
                  key={storyId}
                  id={`story-card-${storyId}`}
                  className={
                    "ns-card ns-clickable storyline-card" +
                    (publicationInfo.isPublished ? " is-published-work" : " is-active-work") +
                    (isActiveFlash ? " ns-card-active" : "") +
                    (isSelected ? " ns-storyline-selected" : "")
                  }
                  title={tooltip}
                  onClick={() => setSelectedCardId(String(storyId))}
                >
                 
                 <div className="storyline-card-main">
          
                 <div className="storyline-cover-slot" aria-label="Story cover">
  <StoryCoverCard
    story={story}
    authorName={displayAuthor.displayName}
    preferWorkingCover
  />
</div>

  <div className="storyline-card-text">
  <div className="storyline-card-title">
    {publicationInfo.isPublished
      ? safeStr(publicationInfo.publishedTitle).trim() ||
        safeStr(story?.title).trim() ||
        t.card_untitled_story
      : safeStr(story?.title).trim() || t.card_untitled_story}
  </div>

  <div className="storyline-byline" style={{ marginTop: 8 }}>
    {publicationInfo.isPublished ? <div className="ns-storylines-avatar ns-storylines-avatarText">{String(displayAuthor.displayName || "?").trim().slice(0, 2).toUpperCase()}</div> : <AuthorAvatar story={story} t={t} />}
    <div className="storyline-byline-text">
      <div className="storyline-author-name">
          <span style={{ fontWeight: 800 }}>{t.card_by} </span>
          {displayAuthor.authorUsername ? (
            <button
              type="button"
              className="ns-link-button"
              title={`${t.title_view_profile} ${displayAuthor.displayName}`}
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/u/${displayAuthor.authorUsername}`, "_blank", "noopener,noreferrer");
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                fontWeight: 900,
                color: "#0f172a",
                textDecoration: "underline",
              }}
            >
              {displayAuthor.displayName}
            </button>
          ) : (
            <span>{displayAuthor.displayName}</span>
          )}
        </div>
      </div>
    </div>

                    <div className="storyline-meta-compact" style={{ marginTop: 8 }}>
                        {publicationInfo.isPublished ? (
                          <span>{getPublishedMetaLine(story, t)}</span>
                        ) : compactMetaLine(story, t) ? (
                          <span>{compactMetaLine(story, t)}</span>
                        ) : (
                          <span className="ns-card-meta">{t.card_storyline_fallback}</span>
                        )}
                      </div>

                      <div className="storyline-status-row" style={{ marginTop: 10 }}>
                        {publicationInfo.isPublished ? (
                          <span className="storyline-status-chip is-published">
                            {publicationInfo.publicationLabel}
                          </span>
                        ) : (
                          <>
                            <span className="storyline-status-chip is-development">
                              {publicationInfo.publicationLabel}
                            </span>
                            <span className="storyline-status-chip is-preview">
                              {publicationInfo.developmentLabel}
                            </span>
                          </>
                        )}
                      </div>

                      {!publicationInfo.isPublished ? (
                        <div className="storyline-dates" style={{ marginTop: 10 }}>
                          {createdAt ? (
                            <div className="ns-card-meta">
                              {t.card_created}: {formatDateTime(createdAt)}
                            </div>
                          ) : (
                            <div className="ns-card-meta" style={{ opacity: 0.75 }}>
                              {t.card_created_unknown}
                            </div>
                          )}
                          {updatedAt ? (
                            <div className="ns-card-meta">
                              {t.card_last_updated}: {formatDateTime(updatedAt)}
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {feedback?.text ? (
                        <div
                          aria-live="polite"
                          style={{
                            marginTop: 10,
                            fontSize: 12.5,
                            fontWeight: 800,
                            color: feedbackColor,
                            background: "rgba(248,250,252,0.82)",
                            border: "1px solid rgba(15,23,42,0.08)",
                            borderRadius: 10,
                            padding: "8px 10px",
                          }}
                        >
                          {feedback.text}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="storyline-card-actions">
                    {!publicationInfo.isPublished && writerRoomAllowed ? (
                      <button
                        type="button"
                        className={"ns-button ns-button-primary" + (isGuestLocked ? " ns-button-outline" : "")}
                        title={isGuestLocked ? t.title_login_required_writer_studio : t.title_enter_writer_studio}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!storyId) return;
                          if (isWriterBusy) return;

                          if (isGuestLocked) {
                            setCardFeedback(storyId, { type: "info", text: t.feedback_login_required_writer_room }, 2400);
                            return goLogin("writer_room_blocked_guest");
                          }

                          enterWriter(story);
                        }}
                        disabled={anyBusy || !storyId}
                        aria-disabled={anyBusy || !storyId ? "true" : "false"}
                        style={{
                          height: 28,
                          padding: "0 10px",
                          fontSize: 11.5,
                          borderRadius: 999,
                          ...(isGuestLocked
                            ? { opacity: 0.65, filter: "grayscale(0.35)", border: "1px solid rgba(15,23,42,0.18)" }
                            : {}),
                        }}
                      >
                        {isWriterBusy ? t.btn_opening : isGuestLocked ? t.btn_login_to_write : t.btn_writer_room}
                      </button>
                    ) : null}

                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!storyId) return;
                        if (isReaderBusy) return;
                        if (hasProgress) return continueReaderView(story);
                        return readerView(story);
                      }}
                      disabled={anyBusy || !storyId}
                      aria-disabled={anyBusy || !storyId ? "true" : "false"}
                      style={{ height: 28, padding: "0 10px", fontSize: 11.5, borderRadius: 999 }}
                      title={t.title_open_reader_view}
                    >
                      {isReaderBusy ? t.btn_opening : readerPrimaryLabel}
                    </button>

                    {hasStoryTeaser ? (
                      <button
                        type="button"
                        className="ns-button ns-button-outline"
                        onClick={(event) => { event.stopPropagation(); setTeaserStoryId(String(storyId)); }}
                        disabled={!storyId}
                        aria-label={lang === "fr" ? "Lire l’accroche du livre" : "Read Story Teaser"}
                      >
                        {lang === "fr" ? "Accroche" : "Story Teaser"}
                      </button>
                    ) : null}

                    {readerHint ? (
                      <button
                        type="button"
                        className="ns-button ns-button-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!storyId) return;
                          if (anyBusy) return;
                          return continueReaderView(story);
                        }}
                        disabled={anyBusy || !storyId}
                        aria-disabled={anyBusy || !storyId ? "true" : "false"}
                        title={readerHint}
                        style={{
                          height: 28,
                          padding: "0 10px",
                          borderRadius: 999,
                          border: "1px solid rgba(22, 163, 74, 0.45)",
                          background: "rgba(22, 163, 74, 0.10)",
                          color: "rgba(21, 128, 61, 0.98)",
                          fontWeight: 900,
                          fontSize: 11.5,
                          letterSpacing: 0.1,
                          boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
                        }}
                      >
                        {readerHint}
                      </button>
                    ) : null}

                    {publicationInfo.isPublished && publicationInfo.amazonHref ? (
                      <button
                        type="button"
                        className="ns-button ns-button-outline storyline-amazon-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer");
                        }}
                        style={{ height: 28, padding: "0 10px", fontSize: 11.5, borderRadius: 999 }}
                      >
                        {publicationInfo.cta}
                      </button>
                    ) : null}

                    <button
                      type="button"
                      className="ns-button ns-button-outline"
                      title={isAuthenticated ? t.title_save_storyline_to_shelf : t.title_login_required_shelf}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!storyId) return;
                        if (isShelfBusy) return;
                        toggleShelf(storyId);
                      }}
                      disabled={isShelfBusy || !storyId}
                      aria-disabled={isShelfBusy || !storyId ? "true" : "false"}
                      style={{
                        height: 28,
                        padding: "0 10px",
                        fontSize: 11.5,
                        borderRadius: 999,
                        ...(!isAuthenticated ? { opacity: 0.65, filter: "grayscale(0.35)" } : {}),
                        ...(isSaved ? { fontWeight: 900 } : {}),
                      }}
                    >
                      {isShelfBusy ? t.btn_saving : isSaved ? t.btn_saved : t.btn_save_to_shelf}
                    </button>
                  </div>
                </article>
              );
            })}

          {!loading && !showGridSkeletons && pageStories.length === 0 && (
            <div className="ns-card">
              <p>{t.no_storylines_filters}</p>
            </div>
          )}
        </div>
      </section>

      {teaserStory && String(teaserStory.storyTeaser || "").trim() ? (
        <StoryTeaserDialog
          story={teaserStory}
          authorName={getStoryPublicationInfo(teaserStory, t).isPublished
            ? getPublishedDisplayAuthorName(teaserStory, getAuthorInfo(teaserStory, t).displayName)
            : getAuthorInfo(teaserStory, t).displayName}
          language={lang}
          onClose={dismissTeaser}
          onRead={() => {
            dismissTeaser();
            readerView(teaserStory);
          }}
        />
      ) : null}

      {filtered.length > PAGE_SIZE && (
        <div className="storylines-pagination">
          <button className="ns-button ns-button-outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← {t.previous}
          </button>
          <span className="ns-card-meta">
            {t.page} {page} {t.of} {totalPages}
          </span>
          <button className="ns-button ns-button-outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            {t.next} →
          </button>
        </div>
      )}
    </div>
  );
}
